import type {
  LegendListProps,
  LegendListRef,
} from '@legendapp/list/react-native';
import LegendList from '-/Custom/LegendList';
import type { Ref } from 'react';
import { Platform, StyleSheet, type ViewStyle } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import ContentBoundary from '@/components/UI/ContentBoundary';
import useThemeStore from '@/stores/useThemeStore';

export type ListProps<TItem> = {
  contentPadding?: Pick<ViewStyle, 'paddingTop' | 'paddingBottom'>;
  data: readonly TItem[];
  fetching?: boolean;
  error?: unknown;
  onRetry?: () => void;
  keyboardScroll?: boolean;
  renderItem: NonNullable<LegendListProps<TItem>['renderItem']>;
  ref?: Ref<LegendListRef>;
} & Omit<LegendListProps<TItem>, 'data' | 'renderItem' | 'children'>;

export default function List<TItem>({
  contentPadding,
  ref: listRef,
  data,
  fetching,
  error,
  onRetry,
  keyboardScroll = true,
  renderItem,
  ListEmptyComponent,
  ListFooterComponent,
  ListHeaderComponent,
  ...props
}: ListProps<TItem>) {
  const theme = useThemeStore((state) => state.theme);
  const keyboardShouldPersistTaps: ListProps<TItem>['keyboardShouldPersistTaps'] =
    'handled';
  const keyboardTapProps =
    Platform.OS === 'web' ? {} : { keyboardShouldPersistTaps };

  const isEmpty = data.length === 0;
  const canCenterEmptyState =
    isEmpty && !ListHeaderComponent && !ListFooterComponent;

  const EmptyComponent = ListEmptyComponent;

  return (
    <LegendList
      {...props}
      {...keyboardTapProps}
      extraData={[theme, props.extraData]}
      ref={listRef}
      style={style.list}
      contentContainerStyle={[
        style.contentContainer,
        canCenterEmptyState ? style.emptyContentContainer : undefined,
        props.contentContainerStyle,
        contentPadding,
      ]}
      ListFooterComponent={ListFooterComponent}
      ListHeaderComponent={ListHeaderComponent}
      renderScrollComponent={
        keyboardScroll
          ? (scrollProps) => <KeyboardAwareScrollView {...scrollProps} />
          : undefined
      }
      data={data}
      renderItem={renderItem}
      ListEmptyComponent={
        <ContentBoundary
          data={null}
          loading={fetching}
          error={error}
          onRetry={onRetry}
          loadingTestID="ListLoading"
          empty={
            typeof EmptyComponent === 'function' ? (
              <EmptyComponent />
            ) : (
              EmptyComponent
            )
          }
        >
          {() => null}
        </ContentBoundary>
      }
    />
  );
}

const style = StyleSheet.create({
  list: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
  },
  emptyContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
